import type { ProductTypeConfig } from '../types/productType.types';
import type { ProductType } from '@/types/store';
import { PRODUCT_TYPES } from '@/lib/constants';

// Import custom SVG icons from assets
import LeadMagnetIcon from '@/assets/images/add-product-icons/add-product-lead-magnet.svg';
import CalendarIcon from '@/assets/images/add-product-icons/add-product-calendar.svg';
import CourseIcon from '@/assets/images/add-product-icons/add-product-course.svg';
import WebinarIcon from '@/assets/images/add-product-icons/add-product-webinar.svg';
import MediaURLIcon from '@/assets/images/add-product-icons/add-product-media-url.svg';
import AIAvatarIcon from '@/assets/images/add-product-icons/add-product-ai-avatar.svg';
import DigitalDownloadIcon from '@/assets/images/add-product-icons/add-product-dd.svg';
import CustomProductIcon from '@/assets/images/add-product-icons/custom-product.svg';
import MembershipIcon from '@/assets/images/add-product-icons/add-product-membership.svg';
import CommunityIcon from '@/assets/images/add-product-icons/add-product-community.svg';
import AffiliateIcon from '@/assets/images/add-product-icons/add-product-affiliate-link.svg';

export const ADD_PRODUCT_CONFIGS: ProductTypeConfig[] = [
    {
        id: PRODUCT_TYPES.LEAD_MAGNET,
        title: 'Collect Emails / Applications',
        description: "Collect Your Audience's Info with a Lead Magnet",
        icon: LeadMagnetIcon,
        enabled: false, // Will be set dynamically based on user data
    },
    {
        id: PRODUCT_TYPES.DIGITAL_DOWNLOAD,
        title: 'Digital Product',
        description: 'PDFs, Guides, Templates, Exclusive Content, eBooks, etc.',
        icon: DigitalDownloadIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.BOOKING,
        title: 'Coaching Call',
        description: 'Book Discovery Calls, Paid Coaching',
        icon: CalendarIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.FULFILLMENT,
        title: 'Custom Product',
        description: '"Ask Me Anything" requests, Audits/Analyses, Video Reviews',
        icon: CustomProductIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.COURSE,
        title: 'eCourse',
        description: 'Create, Host, and Sell your Course within Stan',
        icon: CourseIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.MEMBERSHIP,
        title: 'Recurring Membership',
        description: 'Charge Recurring Subscriptions',
        icon: MembershipIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.WEBINAR,
        title: 'Webinar',
        description: 'Host exclusive coaching sessions or online events with multiple customers',
        icon: WebinarIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.COMMUNITY,
        title: 'Community',
        description: 'Host a free or paid community',
        icon: CommunityIcon,
        enabled: false,
        // TODO: Update message to include actual community name: "You already have an active community: {communityName}."
        disabledTooltip:
            'You already have an active community. Learn how to add your Community to another product <a href="https://help.stan.store/article/359-how-to-invite-customers-to-your-community" target="_blank" class="text-brand-main underline">here</a>.',
    },
    {
        id: PRODUCT_TYPES.LINK,
        title: 'URL / Media',
        description: 'Link to a Website, Affiliate Link, or even Embed Youtube and Spotify content',
        icon: MediaURLIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.STAN_REFERRAL_LINK,
        title: 'Stan Affiliate Link',
        description: 'Refer a friend and receive 20% of their Stan Subscription fee each month!',
        icon: AffiliateIcon,
        enabled: false,
    },
    {
        id: PRODUCT_TYPES.AI_AVATAR,
        title: 'AI Twin',
        description: 'Trained on your content, ready to respond to your audience 24/7',
        icon: AIAvatarIcon,
        enabled: false,
        disabledTooltip: 'You already have an active AI Twin!',
    },
];

export const getProductTypeConfig = (id: ProductType): ProductTypeConfig | undefined => {
    return ADD_PRODUCT_CONFIGS.find(config => config.id === id);
};

export const updateProductTypesEnabled = (
    productTypes: ProductTypeConfig[],
    activeProductTypes: ProductType[]
): ProductTypeConfig[] => {
    // Remove duplicates from activeProductTypes to prevent duplicate keys
    const uniqueActiveProductTypes = [...new Set(activeProductTypes)];

    return productTypes.map(config => ({
        ...config,
        enabled: uniqueActiveProductTypes.includes(config.id),
    }));
};
